<template>
  <div v-if="attachments.length">
    <!-- Compact: inline within comments -->
    <div v-if="compact" class="space-y-1.5">
      <div
        class="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide"
        :class="compactLabelClass"
      >
        <i class="mdi mdi-paperclip text-[12px]"></i>
        <span>{{ compactTitle }}</span>
        <span
          class="px-1 py-0.5 rounded text-[8px] font-bold tabular-nums leading-none"
          :class="compactCountClass"
        >
          {{ attachments.length }}
        </span>
      </div>

      <div
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5"
      >
        <a
          v-for="(att, i) in attachments"
          :key="'att-' + i"
          :href="fileUrl(att)"
          :target="isImage(att) ? '_blank' : undefined"
          rel="noopener noreferrer"
          class="group overflow-hidden rounded-md border transition-colors"
          :class="isImage(att) ? compactImageClass : compactFileRowClass"
          @click="!isImage(att) ? handleDownload(att, $event) : undefined"
        >
          <!-- Image thumbnail -->
          <template v-if="isImage(att)">
            <div class="relative aspect-square bg-slate-100 dark:bg-slate-800">
              <img
                :src="fileUrl(att)"
                :alt="displayFileName(att)"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <div
                class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-primary/50 transition-opacity"
              >
                <i class="mdi mdi-open-in-new text-sm text-white"></i>
              </div>
            </div>
            <p
              class="px-1.5 py-1 text-[9px] font-medium text-slate-500 dark:text-slate-400 truncate border-t border-slate-100 dark:border-slate-700/80"
            >
              {{ displayFileName(att) }}
            </p>
          </template>

          <!-- File chip -->
          <template v-else>
            <div class="flex items-center gap-1.5 p-1.5 min-h-[52px] h-full">
              <span
                class="flex items-center justify-center w-7 h-7 shrink-0 rounded border border-slate-100 dark:border-slate-700"
                :class="iconBg(att)"
              >
                <i :class="[fileIcon(att), 'text-[14px]', iconColor(att)]"></i>
              </span>
              <div class="min-w-0 flex-1">
                <p class="text-[10px] font-semibold text-slate-800 dark:text-slate-100 truncate leading-tight">
                  {{ displayFileName(att) }}
                </p>
                <p class="text-[9px] text-slate-500 truncate leading-tight mt-0.5">
                  {{ typeLabel(att) }}
                  <span v-if="fileSize(att)"> · {{ fileSize(att) }}</span>
                </p>
              </div>
              <i
                class="mdi mdi-download-outline text-[13px] text-slate-400 group-hover:text-primary shrink-0 transition-colors opacity-0 group-hover:opacity-100 sm:opacity-100"
              ></i>
            </div>
          </template>
        </a>
      </div>
    </div>

    <!-- Default: full panel for ticket description -->
    <div
      v-else
      class="overflow-hidden rounded-xl border bg-white dark:bg-slate-900"
      :class="panelBorderClass"
    >
      <div
        class="flex items-center gap-2 px-3 py-2.5 border-b"
        :class="headerClass"
      >
        <i class="mdi mdi-paperclip text-[15px]" :class="iconClass"></i>
        <span class="text-[11px] font-semibold text-slate-800 dark:text-slate-100">
          {{ title || (lang === "ar" ? "المرفقات" : "Attachments") }}
        </span>
        <span
          class="px-1.5 py-0.5 text-[9px] font-bold rounded-md border"
          :class="countBadgeClass"
        >
          {{ attachments.length }}
        </span>
      </div>

      <div class="p-3 space-y-3">
        <div
          v-if="imageItems.length"
          class="grid grid-cols-2 sm:grid-cols-3 gap-2"
        >
          <a
            v-for="(att, i) in imageItems"
            :key="'img-' + i"
            :href="fileUrl(att)"
            target="_blank"
            rel="noopener noreferrer"
            class="group relative overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary transition-colors"
          >
            <div class="aspect-[4/3] bg-slate-100 dark:bg-slate-800">
              <img
                :src="fileUrl(att)"
                :alt="displayFileName(att)"
                class="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div
              class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-primary/40 transition-opacity"
            >
              <i class="mdi mdi-open-in-new text-xl text-white"></i>
            </div>
            <p class="px-2 py-1.5 text-[10px] font-medium text-slate-600 dark:text-slate-300 truncate border-t border-slate-100 dark:border-slate-800">
              {{ displayFileName(att) }}
            </p>
          </a>
        </div>

        <ul v-if="fileItems.length" class="space-y-1.5">
          <li v-for="(att, i) in fileItems" :key="'file-' + i">
            <a
              :href="fileUrl(att)"
              rel="noopener noreferrer"
              class="group flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 hover:border-primary/30 hover:bg-primary/5 transition-colors"
              @click="handleDownload(att, $event)"
            >
              <span
                class="flex items-center justify-center w-9 h-9 shrink-0 rounded-lg border border-slate-100 dark:border-slate-700"
                :class="iconBg(att)"
              >
                <i :class="[fileIcon(att), 'text-[18px]', iconColor(att)]"></i>
              </span>
              <div class="min-w-0 flex-1">
                <p class="text-[11px] font-semibold text-slate-800 dark:text-slate-100 truncate">
                  {{ displayFileName(att) }}
                </p>
                <p class="text-[10px] text-slate-500">
                  {{ typeLabel(att) }}
                  <span v-if="fileSize(att)" class="text-primary font-medium">
                    · {{ fileSize(att) }}
                  </span>
                </p>
              </div>
              <i
                class="mdi mdi-download-outline text-[16px] text-slate-400 group-hover:text-primary transition-colors shrink-0"
              ></i>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import api from "@/services/api";

const props = defineProps({
  attachments: { type: Array, default: () => [] },
  lang: { type: String, default: "ar" },
  title: { type: String, default: "" },
  compact: { type: Boolean, default: false },
  variant: {
    type: String,
    default: "default",
    validator: (v) => ["default", "mine", "other"].includes(v),
  },
});

const imageItems = computed(() =>
  props.attachments.filter((a) => isImage(a)),
);
const fileItems = computed(() =>
  props.attachments.filter((a) => !isImage(a)),
);

const compactTitle = computed(() =>
  props.title ||
  (props.lang === "ar" ? "مرفقات" : "Attachments"),
);

const panelBorderClass = computed(() => {
  if (props.variant === "mine") return "border-primary/25";
  if (props.variant === "other") return "border-secondary/25";
  return "border-slate-200 dark:border-slate-700";
});

const headerClass = computed(() => {
  if (props.variant === "mine")
    return "border-primary/15 bg-primary/5 dark:bg-primary/10";
  if (props.variant === "other")
    return "border-secondary/15 bg-secondary/10 dark:bg-secondary/5";
  return "border-slate-100 dark:border-slate-800 bg-secondary/10 dark:bg-secondary/5";
});

const iconClass = computed(() => {
  if (props.variant === "mine") return "text-primary";
  return "text-secondary";
});

const countBadgeClass = computed(() => {
  if (props.variant === "mine")
    return "bg-white dark:bg-slate-800 text-primary border-primary/20";
  return "bg-white dark:bg-slate-800 text-secondary border-secondary/25";
});

const compactLabelClass = computed(() => {
  if (props.variant === "mine") return "text-primary";
  if (props.variant === "other") return "text-secondary";
  return "text-slate-500";
});

const compactCountClass = computed(() => {
  if (props.variant === "mine")
    return "bg-primary/10 text-primary";
  if (props.variant === "other")
    return "bg-secondary/15 text-secondary";
  return "bg-slate-100 dark:bg-slate-800 text-slate-500";
});

const compactFileRowClass = computed(() => {
  const base =
    "border-slate-200 dark:border-slate-700 hover:border-primary/35";
  if (props.variant === "mine")
    return `${base} bg-white/90 dark:bg-slate-900/70 hover:bg-primary/5 border-primary/15`;
  if (props.variant === "other")
    return `${base} bg-white/90 dark:bg-slate-900/70 hover:bg-secondary/10 border-secondary/25 hover:border-secondary/40`;
  return `${base} bg-slate-50/90 dark:bg-slate-800/50 hover:bg-slate-100/80 dark:hover:bg-slate-800/70`;
});

const compactImageClass = computed(() => {
  const base =
    "border-slate-200 dark:border-slate-700 hover:border-primary transition-colors";
  if (props.variant === "mine")
    return `${base} border-primary/15`;
  if (props.variant === "other")
    return `${base} border-secondary/25 hover:border-secondary/50`;
  return base;
});

function getAuthToken() {
  const match = document.cookie.match(/(?:^|; )auth_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function filenameFromUrl(url) {
  if (!url) return "";
  try {
    return decodeURIComponent(String(url).split("/").pop().split("?")[0]);
  } catch {
    return String(url).split("/").pop().split("?")[0] || "";
  }
}

function resolveAttachmentUrl(url) {
  const value = String(url || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;

  const base = String(api.defaults?.baseURL || "").replace(/\/api\/v1\/?$/i, "");
  const path = value.startsWith("/") ? value : `/${value}`;
  return base ? `${base}${path}` : value;
}

/** API may return a URL string or an attachment object. */
function normalizeAttachment(att) {
  if (typeof att === "string") {
    const url = resolveAttachmentUrl(att);
    return {
      url,
      name: filenameFromUrl(url),
      mime_type: "",
      size: null,
    };
  }

  if (att && typeof att === "object") {
    const url = resolveAttachmentUrl(
      att.url || att.path || att.file_url || att.download_url || "",
    );
    const name =
      String(att.name || att.filename || att.original_name || "").trim() ||
      filenameFromUrl(url);
    return {
      ...att,
      url,
      name,
      mime_type: att.mime_type || att.type || "",
      size: att.size || att.file_size || null,
    };
  }

  return { url: "", name: "", mime_type: "", size: null };
}

function fileUrl(att) {
  return normalizeAttachment(att).url || "#";
}

async function handleDownload(att, event) {
  event?.preventDefault();

  const normalized = normalizeAttachment(att);
  const url = normalized.url;
  if (!url) return;

  const filename = displayFileName(normalized);
  const token = getAuthToken();

  try {
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function rawFileName(att) {
  return String(normalizeAttachment(att).name || "").trim();
}

function genericFileLabel() {
  return props.lang === "ar" ? "ملف" : "File";
}

function displayFileName(att) {
  const raw = rawFileName(att);
  if (!raw) return genericFileLabel();
  if (props.lang === "ar" && /^file$/i.test(raw)) return "ملف";
  return raw;
}

function fileName(att) {
  return rawFileName(att) || "file";
}

function fileSize(att) {
  const bytes = normalizeAttachment(att).size;
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(att) {
  const normalized = normalizeAttachment(att);
  const name = (normalized.name || filenameFromUrl(normalized.url)).toLowerCase();
  const mime = String(normalized.mime_type || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
}

function fileIcon(att) {
  const name = fileName(att).toLowerCase();
  if (name.endsWith(".pdf")) return "mdi mdi-file-pdf-box";
  if (/\.(doc|docx)$/.test(name)) return "mdi mdi-file-word-outline";
  if (/\.(xls|xlsx)$/.test(name)) return "mdi mdi-file-excel-outline";
  return "mdi mdi-file-document-outline";
}

function iconBg(att) {
  const name = fileName(att).toLowerCase();
  if (name.endsWith(".pdf")) return "bg-rose-50/80 dark:bg-rose-900/20";
  if (/\.(doc|docx)$/.test(name)) return "bg-sky-50/80 dark:bg-sky-900/20";
  if (/\.(xls|xlsx)$/.test(name)) return "bg-emerald-50/80 dark:bg-emerald-900/20";
  return "bg-slate-50 dark:bg-slate-800";
}

function iconColor(att) {
  const name = fileName(att).toLowerCase();
  if (name.endsWith(".pdf")) return "text-rose-600 dark:text-rose-300";
  if (/\.(doc|docx)$/.test(name)) return "text-sky-600 dark:text-sky-300";
  if (/\.(xls|xlsx)$/.test(name)) return "text-emerald-600 dark:text-emerald-300";
  return "text-primary";
}

function typeLabel(att) {
  const name = fileName(att).toLowerCase();
  if (name.endsWith(".pdf")) return props.lang === "ar" ? "PDF" : "PDF";
  if (/\.(doc|docx)$/.test(name))
    return props.lang === "ar" ? "Word" : "Word";
  if (/\.(xls|xlsx)$/.test(name))
    return props.lang === "ar" ? "Excel" : "Excel";
  if (isImage(att)) return props.lang === "ar" ? "صورة" : "Image";
  return props.lang === "ar" ? "ملف" : "File";
}
</script>
